/**
 * Lookup from Slack emoji short names (e.g. "trophy") to their unicode
 * character (e.g. "🏆"), built from emoji-datasource.
 *
 * The raw JSON (~1.3 MB) lands in its own async chunk and is fetched only
 * when a Slack message control asks for it via `loadEmojiMap`, never at
 * module evaluation: this module sits in the main graph through
 * `slackTransformers`, so an eager import would pull the chunk onto every
 * page. Until the data arrives the transformer leaves `:code:` as text.
 *
 * Slack accepts native unicode emoji when publishing messages, so converting
 * colon-codes to unicode for editor display is safe — the round-tripped value
 * works identically with the Slack API.
 */

interface EmojiEntry {
  unified: string;
  short_name: string;
  short_names: string[];
}

function unifiedToChar(unified: string): string {
  return String.fromCodePoint(
    ...unified.split("-").map((hex) => parseInt(hex, 16)),
  );
}

// Exported as a plain object so callers hold a stable reference that gets
// populated in-place once the async chunk finishes loading.
const emojiMap: Record<string, string> = {};

let loading: Promise<boolean> | null = null;
let loaded = false;

/**
 * Loads the table once and shares the load between callers. Resolves to
 * whether the table is available. A chunk that fails to download resolves
 * false instead of rejecting — that is a network problem, not ours — and
 * clears the shared promise so the next caller may try again.
 */
export function loadEmojiMap(): Promise<boolean> {
  if (loaded) return Promise.resolve(true);
  loading ??= import("emoji-datasource/emoji.json")
    .then(({ default: emojiData }) => {
      for (const entry of emojiData as EmojiEntry[]) {
        const char = unifiedToChar(entry.unified);
        emojiMap[entry.short_name] = char;
        for (const alias of entry.short_names) {
          emojiMap[alias] = char;
        }
      }
      loaded = true;
      return true;
    })
    .catch(() => {
      loading = null;
      return false;
    });
  return loading;
}

export default emojiMap;
